"use client";

import { useState, useEffect } from "react";
import { useNotification } from "@/components/NotificationProvider";
import {
  ImpersonationRole,
  ImpersonationMemberType,
} from "@/lib/auth/ImpersonationTypes";

export interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedRole: ImpersonationRole | null;
  impersonatedMemberType: ImpersonationMemberType | null;
  impersonatedBy: string | null;
  impersonatedAt: string | null;
}

export function useImpersonation() {
  const [impersonationState, setImpersonationState] =
    useState<ImpersonationState>({
      isImpersonating: false,
      impersonatedRole: null,
      impersonatedMemberType: null,
      impersonatedBy: null,
      impersonatedAt: null,
    });
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  // Load impersonation state on mount (only in browser)
  useEffect(() => {
    // Only load state if we're in the browser
    if (typeof window !== "undefined") {
      loadImpersonationState();
    }
  }, []);

  const loadImpersonationState = async () => {
    try {
      const response = await fetch("/api/impersonation/status");
      if (response.ok) {
        const state = await response.json();
        setImpersonationState(state);
      }
    } catch (error) {
      console.error("Failed to load impersonation state:", error);
    }
  };

  const startImpersonation = async (
    role: ImpersonationRole,
    tenantId: string,
    memberType?: ImpersonationMemberType
  ) => {
    setLoading(true);
    try {
      // API requires both role and memberType; default sensibly if not provided
      const payload = {
        role,
        tenantId,
        memberType:
          memberType ||
          (role === "member"
            ? ("owner" as ImpersonationMemberType)
            : ("public" as ImpersonationMemberType)),
      };
      const response = await fetch("/api/impersonation/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await loadImpersonationState();
        notify({
          type: "success",
          message: `Now viewing as ${role}`,
        });
        return true;
      } else {
        const error = await response.json();
        notify({
          type: "error",
          message: error.error || "Failed to start impersonation",
        });
        return false;
      }
    } catch (error) {
      notify({
        type: "error",
        message: "Failed to start impersonation",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonation = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/impersonation/stop", {
        method: "POST",
      });

      if (response.ok) {
        await loadImpersonationState();
        notify({
          type: "success",
          message: "Stopped impersonation",
        });
        return true;
      } else {
        const error = await response.json();
        notify({
          type: "error",
          message: error.error || "Failed to stop impersonation",
        });
        return false;
      }
    } catch (error) {
      notify({
        type: "error",
        message: "Failed to stop impersonation",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    impersonationState,
    loading,
    startImpersonation,
    stopImpersonation,
    loadImpersonationState,
  };
}
